import {
  Button,
  Card,
  Form,
  Input,
  message,
  Table,
  Popconfirm,
  Modal,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { API } from "../../config/Api";
import { GET, POST, PUT, DELETE } from "../../utils/apiRequest";

const { TextArea } = Input;

interface CategoryData {
  id: number;
  name: string;
  description: string;
  created_at: string;
}

const ExpenseCategory = () => {
  const [form] = Form.useForm();
  const user = useSelector((state: any) => state?.User?.user);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryData | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    getCategories();
    console.log("responseresponse");
  }, []);

  const getCategories = async () => {
    try {
      setTableLoading(true);
      const response: any = await GET(
        API.GET_FINANCE_CATEGORY_BY_TYPE +
          "expense?officeId=" +
          user?.office?.id,
        null
      );
      console.log(response);
      if (response?.status) {
        setCategories(response?.data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setTableLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const requestPayload: any = {
        name: values?.category_name,
        description: values?.description,
        office_id: parseInt(user?.office?.id) || 0,
        created_by: parseInt(user?.user?.id) || parseInt(user?.id) || 0,
        type: "expense",
        is_active: true,
      };

      console.log("User data:", user);
      console.log("Request payload:", requestPayload);

      let response: any;
      if (editingCategory) {
        requestPayload.id = parseInt(String(editingCategory?.id));
        // Update existing category
        response = await PUT(API.UPDATE_FINANCE_CATEGORY, requestPayload);
      } else {
        // Create new category
        response = await POST(API.CREATE_FINANCE_CATEGORY, requestPayload);
      }

      console.log("API Response:", response);
      if (response?.status) {
        message.success(
          editingCategory
            ? "Category updated successfully"
            : "Category created successfully"
        );
        form.resetFields();
        setEditingCategory(null);
        setIsModalOpen(false);
        await getCategories();
      } else {
        message.error(
          editingCategory
            ? "Failed to update category"
            : "Failed to create category"
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Error saving category:", error);
      message.error("Something went wrong");
      setLoading(false);
    }
  };

  const handleEdit = (category: CategoryData) => {
    setEditingCategory(category);
    form.setFieldsValue({
      category_name: category.name,
      description: category.description,
    });
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setTableLoading(true);
      const response: any = await DELETE(API.DELETE_FINANCE_CATEGORY + id);
      if (response?.status) {
        message.success("Category deleted successfully");
        await getCategories();
      } else {
        message.error("Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      message.error("Something went wrong");
    } finally {
      setTableLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "id",
      key: "id",
      render: (_: any, __: any, index: any) => index + 1,
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: any) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: CategoryData) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="screenBox">
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h5 className="user-heading" style={{ margin: 0 }}>
            Expense Categories
          </h5>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add Category
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={categories}
          loading={tableLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
          }}
        />
      </Card>

      <Modal
        title={
          editingCategory ? "Edit Expense Category" : "Add Expense Category"
        }
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          hideRequiredMark
        >
          <Row>
            <Col md={12}>
              <Form.Item
                name="category_name"
                label="Category Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter category name",
                  },
                ]}
              >
                <Input placeholder="E.g., Utilities, Equipment, Maintenance" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item
                name="description"
                label="Description"
                rules={[
                  {
                    required: true,
                    message: "Please enter description",
                  },
                ]}
              >
                <TextArea rows={3} placeholder="Enter category description" />
              </Form.Item>
            </Col>
          </Row>
          <div className="text-end">
            <Button
              type="default"
              onClick={handleModalCancel}
              style={{ marginRight: "8px" }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingCategory ? "Update Category" : "Create Category"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ExpenseCategory;
